import { DSOK_DEVICE_SERVER_PORT_START } from "app/utils/constants/settings";
import { sanitizeLogoUrlString } from "app/pages/customers/dsok/utils/sanitizeLogoUrlString";

/**
 * Maps DSOK customer-detail route path suffix to section id and dsokSettings path.
 * Used for section-scoped update: only the subtree at path is replaced with the section payload.
 * Sections with path: [] do not persist to dsokSettings; getSectionUpdatePayload returns null for them.
 *
 * path: array of keys into dsokSettings, e.g. ["customer"] or ["customer", "locations"].
 * The payload from the section replaces the value at that path.
 *
 * This config is the canonical list of all DSOK customer-detail sections (menu, routing, breadcrumbs).
 */
export const DSOK_CUSTOMER_DETAIL_SECTION_CONFIG = {
  settings: {
    sectionId: "settings",
    path: ["customer"],
  },
  "ui-settings": {
    sectionId: "ui-settings",
    path: ["customer", "customerUISettings"],
  },
  locations: {
    sectionId: "locations",
    path: ["customer", "locations"],
  },
  devices: {
    sectionId: "devices",
    path: ["customer", "devices"],
  },
  "application-settings": {
    sectionId: "app-settings",
    path: ["settings"],
  },
  colors: {
    sectionId: "colors",
    path: ["settings", "colors"],
  },
  products: {
    sectionId: "products",
    path: ["settings", "smartkiosk", "productSettings"],
  },
  cart: {
    sectionId: "cart",
    path: ["settings", "smartkiosk", "cartSettings"],
  },
  categories: {
    sectionId: "categories",
    path: ["settings", "smartkiosk", "categoriesDisplaySettings"],
  },
  "dynamic-components": {
    sectionId: "dynamic-components",
    path: ["settings", "smartkiosk", "dynamicComponents"],
  },
  integrations: {
    sectionId: "integrations",
    path: ["integrations"],
  },
  translations: {
    sectionId: "translations",
    path: ["settings", "translations"],
  },
  maintenance: {
    sectionId: "maintenance",
    path: ["maintenance"],
  },
  billing: {
    sectionId: "billing",
    // Uložené pod `settings` (nie top-level) — Symfony/MySQL zachováva len známe top-level
    // kľúče (customer/settings/maintenance/integrations); nový top-level `billing` by zahodil.
    path: ["settings", "billing"],
  },
};

/**
 * @param {string} pathname - e.g. /dsok/customer-detail/my-customer/settings
 * @returns {{ sectionId: string, path: string[] } | null}
 */
export function getActiveSectionFromPathname(pathname) {
  if (!pathname || typeof pathname !== "string") return null;
  const match = pathname.match(/\/dsok\/customer-detail\/[^/]+\/([^/]+)/);
  const suffix = match ? match[1] : null;
  if (!suffix) return null;
  const config = DSOK_CUSTOMER_DETAIL_SECTION_CONFIG[suffix];
  return config ? { sectionId: config.sectionId, path: config.path } : null;
}

/**
 * Set value at path in object immutably. path = ["customer", "locations"].
 */
export function setAtPath(obj, path, value) {
  if (!path || path.length === 0) return value;
  const [head, ...rest] = path;
  const next =
    rest.length === 0
      ? value
      : setAtPath(obj && obj[head] ? { ...obj[head] } : {}, rest, value);
  return { ...obj, [head]: next };
}

/**
 * Normalize section payload for backend (location ids, device ids and server ports).
 * Mutates payload in place; call with a deep clone if you need to keep original.
 */
export function normalizeSectionPayload(sectionId, payload) {
  if (sectionId === "categories" || sectionId === "dynamic-components") {
    if (
      payload != null &&
      typeof payload === "object" &&
      !Array.isArray(payload) &&
      Object.keys(payload).length === 0
    ) {
      return "";
    }
    if (payload == null || typeof payload !== "object") {
      return "";
    }
    // Already the array (path ends at categoriesDisplaySettings, so value = array)
    if (Array.isArray(payload.categoriesDisplaySettings)) {
      return payload.categoriesDisplaySettings;
    }
    // Unwrap if payload was incorrectly double-wrapped
    const inner =
      payload.categoriesDisplaySettings &&
      payload.categoriesDisplaySettings.categoriesDisplaySettings;
    if (Array.isArray(inner)) {
      return inner;
    }
    const keys = Object.keys(payload);
    const hasNumericKeys =
      keys.length > 0 && keys.every((k) => /^\d+$/.test(String(k)));
    if (hasNumericKeys) {
      const sortedKeys = keys.slice().sort((a, b) => Number(a) - Number(b));
      const extracted = sortedKeys
        .map((k) => payload[k])
        .filter((v) => v != null && typeof v === "object");
      // return extracted[0];
      return extracted.map((o) => ({ ...o }));
    }
    // Flat object → single-element array
    return [{ ...payload }];
  }
  if (!payload || typeof payload !== "object") return payload;
  if (sectionId === "locations") {
    Object.keys(payload).forEach((key) => {
      if (payload[key] && typeof payload[key] === "object") {
        payload[key].id = +key;
        // Notifikácie objednávok sa presunuli na úroveň zákazníka
        // (settings.orderNotifications) a nemajú príjemcov - posielajú sa
        // vždy guestovi, nikdy zákazníkovi. Starý kľúč tu preto pri uložení
        // odstraňujeme. Sekcia devices ostáva, tam sú príjemcami admini.
        if (payload[key].notifications?.orders) {
          delete payload[key].notifications.orders;
        }
      }
    });
    return payload;
  }
  if (sectionId === "settings") {
    if (typeof payload.mainLogo === "string") {
      payload.mainLogo = sanitizeLogoUrlString(payload.mainLogo);
    }
    if (typeof payload.tableImage === "string") {
      payload.tableImage = sanitizeLogoUrlString(payload.tableImage);
    }
    return payload;
  }
  if (sectionId === "devices") {
    Object.keys(payload).forEach((key) => {
      const dev = payload[key];
      if (dev && typeof dev === "object") {
        dev.id = +key;
        const posPort = DSOK_DEVICE_SERVER_PORT_START + (+key - 1) * 16;
        const ekasaPort = posPort + 1;
        const generalServerPort = ekasaPort + 1;
        // Keep payload shape compatible with backend even when nested blocks are missing.
        dev.pos =
          dev.pos && typeof dev.pos === "object" && !Array.isArray(dev.pos)
            ? dev.pos
            : {};
        dev.ekasa =
          dev.ekasa && typeof dev.ekasa === "object" && !Array.isArray(dev.ekasa)
            ? dev.ekasa
            : {};
        dev.generalServer =
          dev.generalServer &&
          typeof dev.generalServer === "object" &&
          !Array.isArray(dev.generalServer)
            ? dev.generalServer
            : {};
        dev.pos.serverPort = posPort;
        dev.ekasa.serverPort = ekasaPort;
        dev.generalServer.serverPort = generalServerPort;
      }
    });
    return payload;
  }
  return payload;
}
